// Per-stage deployment config — what each link (SST stage) shows. BenBet v1
// (v1-plan §8): ONE codebase, two stages (`dev` = the demo, `v1` = the v1
// link); every difference between them lives here, never in a code fork, so
// Ben's work and every feature reach both links automatically.
//
// Read by every layer, all keyed by the stage name:
//   • app-api — `APP_STAGE` env (set on every Lambda by infra): the widget
//     reader 404s a widget the stage doesn't serve.
//   • app-web — `VITE_APP_STAGE` (baked in at build): React hides disallowed
//     modules from loaded boards; the build injects `window.bentoDeployment`
//     so the legacy menu in main.js shows only allowed widgets + hides the tour.
//   • infra — the data-server URLs handed to the Lambdas.
//
// PURE: no window, no AWS.

import { WIDGET_TITLES, type WidgetKey } from "../widgets/registry.js";

export interface Deployment {
  /** The widgets this link serves: "all", or an allow-list. A v1 widget joins
   *  only once its real data is wired — the "real data only" rule, enforced by
   *  app-api's guard test (every listed key must have a real builder). */
  widgets: "all" | readonly WidgetKey[];
  /** Show the guided tour? Off on v1: the tour places ~19 modules, most of
   *  which v1 doesn't serve (QUESTIONS.md Q-004 — Ben decides v1's tour). */
  tour: boolean;
  /** Base URL of our football-stats data server (public; auth is a secret). */
  fsBaseUrl: string;
  /** Whose Cognito user pool this link signs in against: its own stage name =
   *  it creates the pool; another stage's = it shares that pool (same logins). */
  userPoolStage: string;
}

const FS_BASE_URL = "https://vzocxi59jh.execute-api.eu-west-1.amazonaws.com";

export const DEPLOYMENTS = {
  // The demo link (bento.variancedev.org): every widget, real or dummy.
  dev: { widgets: "all", tour: true, fsBaseUrl: FS_BASE_URL, userPoolStage: "dev" },
  // The v1 link (benbet.variancedev.org): real data only. Grows as sources land
  // (v1-plan §13): 2 → +xG Table/Trajectory (our data server, StatsBomb xG) → +First Goal/GTB (events)
  // → +Winner/Movement (market). Shares the demo's user pool so the same people
  // sign in with the same password; it gets its own pool once it moves to its
  // own (prod) account.
  v1: {
    widgets: ["clean-sheets", "over-under-rates", "xg-table", "performance-trajectory"],
    tour: false,
    fsBaseUrl: FS_BASE_URL,
    userPoolStage: "dev",
  },
} as const satisfies Record<string, Deployment>;

export type DeploymentStage = keyof typeof DEPLOYMENTS;

/**
 * The config for a stage. Unset/empty → `dev` (local dev, tests — the demo's
 * behaviour). An unknown stage throws, so a typo can't silently deploy a link
 * with the wrong widget set.
 */
export function getDeployment(stage: string | undefined): Deployment {
  if (!stage) return DEPLOYMENTS.dev;
  if (!Object.prototype.hasOwnProperty.call(DEPLOYMENTS, stage)) {
    throw new Error(
      `Unknown stage "${stage}" — add it to DEPLOYMENTS in packages/shared/src/config/deployments.ts`,
    );
  }
  return DEPLOYMENTS[stage as DeploymentStage];
}

/** Does this deployment serve the widget? */
export function isWidgetEnabled(deployment: Deployment, key: WidgetKey): boolean {
  return deployment.widgets === "all" || deployment.widgets.includes(key);
}

/** Module titles this deployment serves, or null for "all" (the menu filter). */
export function enabledWidgetTitles(deployment: Deployment): string[] | null {
  return deployment.widgets === "all" ? null : deployment.widgets.map((k) => WIDGET_TITLES[k]);
}
